import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, MapPin, Clock, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const contactInfo = [
  {
    icon: Mail,
    title: 'Email',
    content: 'hello@quickfinance.io',
    subtext: 'We reply within 24 hours'
  },
  {
    icon: MapPin,
    title: 'Address',
    content: 'London',
    subtext: 'United Kingdom'
  },
  {
    icon: Clock,
    title: 'Hours',
    content: 'Mon-Sat 9am-6pm',
    subtext: 'Closed Sundays'
  }
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleApplyClick = () => {
    navigate('/#apply');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSuccess(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <Header />

      {/* Hero */}
      <section className="py-24 sm:py-32 bg-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Contact Info */}
            <div>
              <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">Contact Us</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight leading-tight mb-6 font-['Outfit']">
                Get in Touch
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-12">
                Have questions about our funding solutions? Our friendly UK-based team is here to help you every step of the way.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((info, i) => (
                  <div key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500 mb-1">{info.title}</div>
                      <div className="font-semibold text-slate-900">{info.content}</div>
                      <div className="text-sm text-slate-500">{info.subtext}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map placeholder */}
              <div className="mt-12 bg-slate-100 rounded-2xl h-64 flex items-center justify-center border border-slate-200">
                <div className="text-center text-slate-500">
                  <MapPin className="w-8 h-8 mx-auto mb-2" />
                  <span>London, United Kingdom</span>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div>
              {success ? (
                <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-success">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">Message Sent!</h3>
                  <p className="text-slate-600 mb-6">
                    Thank you for contacting us. Our team will get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => { setSuccess(false); setFormData({ name: '', email: '', phone: '', message: '' }); }}
                    variant="outline"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-form">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Send Us a Message</h2>
                  <p className="text-slate-600 text-sm mb-8">Fill out the form below and we'll get back to you shortly.</p>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="John Smith"
                        required
                        className="mt-1.5"
                        data-testid="input-contact-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="john@example.com"
                        required
                        className="mt-1.5"
                        data-testid="input-contact-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="07123 456789"
                        className="mt-1.5"
                        data-testid="input-contact-phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm font-medium text-slate-700">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="How can we help you?"
                        required
                        rows={5}
                        className="mt-1.5"
                        data-testid="input-contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12 text-base font-medium btn-primary"
                      data-testid="submit-contact-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-slate-900" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6 font-['Outfit']">
            Ready to Apply for Funding?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get started with your application today and receive a decision in as little as 4 hours.
          </p>
          <Button 
            size="lg" 
            onClick={handleApplyClick}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8" 
            data-testid="contact-apply-btn"
          >
            Apply Now <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
